<template>
  <div
    :class="{
      'card': true,
      'is-smallshadow': true,
      'complete': isFormComplete
    }">
    <div
      :class="{
        'card-body': true,
        'form-readonly': readonly
      }">
      <h2
        :class="{
          'heading-bigger mb-4': !readonly,
          'heading-small text-grey': readonly
        }">
        {{ $t('inquiryFinancialInfo.financialData') }}
      </h2>

      <div v-if="allDataLoaded">
        <div :class="{ 'width-narrow': !readonly }">
          <div class="form-group">
            <label class="wide" for="has-incomes">{{ $t('inquiryFinancialInfo.DoYouHaveSourcesOfIncome') }}</label>

            <template v-if="!readonly">
              <validation-provider
                rules="required"
                name="has-incomes"
                v-slot="{ errors }">
                <f-form-radio
                  :value="true"
                  v-model="hasIncomes">
                  {{ $t('ui.yes') }}
                </f-form-radio>
                <f-form-radio
                  :value="false"
                  v-model="hasIncomes">
                  {{ $t('ui.no') }}
                </f-form-radio>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </template>
            <template v-else>
              <template v-if="hasIncomes">
                {{ $t('ui.yes') }}
              </template>
              <template v-else>
                {{ $t('ui.no') }}
              </template>
            </template>
          </div>

          <div class="flex">
            <f-button
              v-if="!readonly"
              :disabled="!hasIncomes"
              theme="primary"
              class="mr-1 mb-1"
              @click.prevent="$bus.$emit('inquiry-income-popup-show', null, allIncomeTypes, allContractTypes, allSettlementTypes, allCurrencies, allIndustries, allBanks, allLiabilities);">
              {{ $t('inquiryFinancialInfo.addSourceOfIncome') }}
            </f-button>

            <template v-if="(hasIncomes && data.incomes.length && businessActivityCurrentIncomes.length)">
              <f-button
                v-if="!readonly"
                :disabled="!(hasIncomes && data.incomes.length && businessActivityCurrentIncomes.length)"
                :title="!data.incomes.length ? this.$t('inquiryFinancialInfo.firstAddFinancialCommitment') : ''"
                theme="dark"
                @click.prevent="$bus.$emit('inquiry-liability-popup-show', null, allBanks, allLiabilities, allCurrencies, allIncomeTypes, businessActivityCurrentIncomes);">
                {{ $t('inquiryFinancialInfo.addFinancialCommitment') }}
              </f-button>
              <div class="flex mb-1 ml-1">
                <f-tooltip
                  v-if="!readonly"
                  :parentElementClass="'card-body'"
                  :tooltipName="'liablity'"
                  :tooltipText="$t('inquiryFinancialInfo.youCanAddOnlyToSourceIncome')"/>
              </div>
            </template>
          </div>
        </div>

        <div class="incomes-wrapper">
          <div
            v-for="(income, index) of data.incomes"
            :key="'income-' + index"
            :class="{
              'flex flex-align-items-center': true,
              'mt-2 pb-2': true,
              'border-bottom': index < data.incomes.length - 1
            }">

            <div class="full-width">
              <div class="items-list flex flex-align-items-center">
                <div class="item-index pr-1 mb-2-sm">
                  <strong>{{ index + 1 }}.</strong>
                </div>
                <div class="item-data mr-2">
                  <strong>
                    {{ getIncomTypeName(income.income_type_id) }}
                    <template v-if="getIncomeType(income.income_type_id) === 'OTHER'">
                      <br><span>{{ income.income_other_name }}</span>
                    </template>
                  </strong>
                </div>
                <div class="item-data mr-2">
                  <template v-if="getIncomeType(income.income_type_id) === 'BUSINESS_ACTIVITY'">
                    <template v-if="getSettlementType(income.settlement_type_id) === 'FIXED_AMOUNT_TAX'">
                      <small>{{ $t('inquiryFinancialInfo.taxRate') }}: </small><br>
                      <strong class="text-primary">{{ income.tax_rate }}%</strong>
                    </template>
                    <template v-else-if="getSettlementType(income.settlement_type_id) === 'LUMP_SUM'">
                      <small>{{ $t('inquiryFinancialInfo.revenueThisYear') }}: </small><br>
                      <strong class="text-primary">{{ income.revenue_amount_current_year }} {{getIncomeCurrency(income)}}</strong>
                    </template>
                    <template v-else>
                      <small>{{ $t('inquiryFinancialInfo.incomeThisYear') }}: </small><br>
                      <strong class="text-primary">{{ income.income_amount_current_year }} {{getIncomeCurrency(income)}}</strong>
                    </template>
                  </template>
                  <template v-else>
                    <small>{{ $t('inquiryFinancialInfo.income') }}: </small><br>
                    <strong class="text-primary">{{ income.income_amount }} {{getIncomeCurrency(income)}}</strong>
                  </template>
                </div>
                <div class="item-data">
                  <template v-if="getIncomeType(income.income_type_id) === 'BUSINESS_ACTIVITY'">
                    <small>{{ $t('inquiryFinancialInfo.settlementMethod') }}: </small><br>
                    <strong class="text-primary">{{ getSettlementTypeName(income.settlement_type_id) }}</strong>
                  </template>
                  <template v-else-if="getIncomeType(income.income_type_id) === 'CONTRACT' && allIncomeTypes.find(ele => ele.id === income.income_type_id).show_contract_type">
                    <small>{{ $t('inquiryFinancialInfo.contractType') }}: </small><br>
                    <strong class="text-primary">{{ getContractTypeName(income.contract_type_id) }}</strong>
                  </template>
                </div>
                <div
                  v-if="!readonly"
                  class="item-options ml-2">
                  <f-dropdown
                    theme="minimal"
                    type="more"
                    :items="[
                      {
                        type: 'button',
                        disabled: false,
                        event: 'inquiry-income-edit',
                        param: income,
                        label: $t('ui.edit'),
                        name: 'edit'
                      },
                      {
                        type: 'button',
                        disabled: false,
                        event: 'inquiry-income-remove',
                        param: index,
                        label: $t('ui.remove'),
                        theme: 'danger',
                        name: 'delete'
                      }
                    ]"/>
                </div>
              </div>

              <div
                :id="'income-' + index + '-liabilities'"
                class="items-nested-wrapper">
                <div
                  v-if="income.liabilities.length"
                  class="mt-3 mb-2">
                  <strong>{{ $t('inquiryFinancialInfo.financialLiabilities') }}:</strong>
                </div>
                <div
                  v-for="(liability, subindex) of income.liabilities"
                  :key="'income-liability' + subindex"
                  :class="{
                    'border-bottom': subindex < income.liabilities.length - 1
                  }">
                  <div class="items-list items-list-nested flex flex-align-items-center full-width py-1">
                    <div class="item-index pr-1">
                        <strong>{{ index + 1 }}.{{ subindex + 1 }}</strong>
                    </div>
                    <div class="item-data mr-2">
                      <strong>{{ getLiabilityName(liability.liability_id)}}</strong>
                    </div>
                    <div class="item-data mr-2">
                      <small>{{ getLiabilityLabel(liability.liability_id) }}: </small><br>
                      <strong class="text-primary">{{ liability.instalment_amount }} PLN</strong>
                    </div>
                    <div class="item-data">
                      <template v-if="liability.liability_id !== 8">
                        <small>{{ $t('inquiryFinancialInfo.forRepayment') }}: </small><br>
                        <strong class="text-primary">{{ liability.payoff_amount }} PLN</strong>
                      </template>
                    </div>
                    <div
                      v-if="!readonly"
                      class="item-options ml-2">
                      <f-dropdown
                        theme="minimal"
                        type="more"
                        :items="[
                          {
                            type: 'button',
                            disabled: false,
                            event: 'inquiry-income-liability-edit',
                            param: { liability, subindex, income, index },
                            label: $t('inquiryFinancialInfo.editCommitment'),
                            name: 'edit'
                          },
                          {
                            type: 'button',
                            disabled: false,
                            event: 'inquiry-income-liability-remove',
                            param: { income, subindex },
                            label: $t('inquiryFinancialInfo.deleteCommitment'),
                            theme: 'danger',
                            name: 'delete'
                          }
                        ]"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!allDataLoaded && !loadError"
        class="loading-spinner-wrapper">
        <div class="loading-spinner pb-3 pt-3">
          <span class="loading-spinner-content">
            {{ $t('ui.loadingDataInProgress') }}&hellip;
          </span>
        </div>
      </div>
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>

    <div
      v-if="!readonly && allDataLoaded"
      class="card-footer pt-3 pb-4 text-right">
      <f-button
        theme="primary"
        withArrow
        big
        @click.prevent="goToNextTab">
        {{ $t('ui.next') }}
      </f-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';


export default {
  name: 'inquiry-financial-info-form',
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isDuplicate: {
      type: Boolean,
      default: false
    },
    incomes: {
      type: Array,
      required: true
    },
    allLiabilities: {
      type: Array,
      default: () => []
    },
    allBanks: {
      type: Array,
      default: () => []
    },
    allCurrencies: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.industriesLoaded && this.incomeTypesLoaded && this.contractTypesLoaded && this.settlementTypesLoaded;
    },
    isFormComplete () {
      let isComplete = false;
      if (this.hasIncomes === false) {
        isComplete = true;
      } else if (this.hasIncomes) {
        isComplete = this.data.incomes.length > 0;
      }

      if (this.allDataLoaded && (isComplete !== this.previousIsCompleteSet)) {
        Vue.set(this, 'previousIsCompleteSet', isComplete);
        this.$bus.$emit('inquiry-form-part-complete', this.index, isComplete, 'financial-info');
      }

      return isComplete;
    },
    businessActivityCurrentIncomes () {
      let incomesCopy = [...this.data.incomes];

      return incomesCopy.filter((income, index) => {
        income.index = index;
        let incomeTypeID = income.income_type_id;
        let incomeTypeData = this.allIncomeTypes.filter(incomeType => incomeType.id === incomeTypeID);

        if (incomeTypeData && incomeTypeData[0] && incomeTypeData[0].type === 'BUSINESS_ACTIVITY') {
          return true;
        }

        return false;
      });
    }
  },
  data() {
    return {
      data: {
        incomes: []
      },
      loaded: false,
      loadError: false,
      hasIncomes: null,
      allIndustries: [],
      industriesLoaded: false,
      allIncomeTypes: [],
      incomeTypesLoaded: false,
      allContractTypes: [],
      contractTypesLoaded: false,
      allSettlementTypes: [],
      settlementTypesLoaded: false,
      previousIsCompleteSet: false
    };
  },
  mounted () {
    this.$bus.$on('inquiry-income-popup-submit', this.saveIncome);
    this.$bus.$on('inquiry-income-edit', this.editIncome);
    this.$bus.$on('inquiry-income-remove', this.removeIncome);
    this.$bus.$on('inquiry-income-liability-edit', this.editIncomeLiability);
    this.$bus.$on('inquiry-income-liability-remove', this.removeIncomeLiability);
    this.$bus.$on('inquiry-income-liability-popup-submit', this.saveIncomeLiability);

    this.loadAdditionalData();

    Vue.nextTick(() => {
      Vue.set(this.data, 'incomes', JSON.parse(JSON.stringify(this.incomes)));

      if (this.isEdit || this.isDuplicate) {
        this.hasIncomes = this.incomes.length > 0;
      }
    });

    this.loaded = true;
  },
  methods: {
    goToNextTab () {
      this.$bus.$emit('inquiry-form-go-next-tab', this.index + 1);
    },
    saveIncome (incomeData, isIncomeEdit) {
      if (!isIncomeEdit) {
        this.data.incomes.push(incomeData);
      }
    },
    getIncomeType (typeID) {
      if (!typeID) {
        return false;
      }

      return this.allIncomeTypes.find(type => type.id === typeID).type;
    },
    getIncomTypeName (typeID) {
      if (!typeID) {
        return this.$t('ui.none');
      }

      return this.allIncomeTypes.find(type => type.id === typeID).name;
    },
    getSettlementTypeName(settlementTypeID) {
      if (settlementTypeID) {
        let settlement = this.allSettlementTypes.find(item => item.id === settlementTypeID);
        if (settlement) {
          return settlement.name;
        }
      }

      return '';
    },
    getSettlementType(settlementTypeID) {
      if (settlementTypeID) {
        let settlement = this.allSettlementTypes.find(item => item.id === settlementTypeID);
        if (settlement) {
          return settlement.type;
        }
      }

      return '';
    },
    getContractTypeName (contracTypeID) {
      if (contracTypeID) {
        let contract = this.allContractTypes.find(item => item.id === contracTypeID);
        if (contract) {
          return contract.name;
        }
      }

      return '';
    },
    getIncomeCurrency (income) {
      if (income) {
        let currency = this.allCurrencies.find(item => item.id === income.currency_id);
        if (!currency) {
          return '';
        }

        if (currency.code !== 'OTHER') {
          return currency.code;
        }

        return income.currency_other_name;
      }

      return '';
    },
    getLiabilityName (liabilityID) {
      let liabilityItem = this.allLiabilities.find(item => item.id === liabilityID);
      if (!liabilityID || !liabilityItem) {
        return '-';
      }

      return liabilityItem.name;
    },
    getLiabilityLabel (liabilityID) {
      let liablity = this.allLiabilities.find(item => item.id === liabilityID);

      if (!liabilityID || !liablity || !liablity.label) {
        return this.$t('inquiryFinancialInfo.installmentAmount');
      }

      return liablity.label;
    },
    editIncome (income) {
      this.$bus.$emit('inquiry-income-popup-show', income, this.allIncomeTypes, this.allContractTypes, this.allSettlementTypes, this.allCurrencies, this.allIndustries, this.allBanks, this.allLiabilities);
    },
    removeIncome (incomeIndex) {
      Vue.swal({
        title: this.$t('inquiryFinancialInfo.areYouSureYouWantToRemoveSourceIncome'),
        text: '',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yesRemove'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.data.incomes.splice(incomeIndex, 1);
          Vue.swal({
            title: this.$t('inquiryFinancialInfo.sourceIncomeHasBeenRemove'),
            html: this.$t('inquiryFinancialInfo.offerMustBeRecorded'),
            icon: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true
          })
        }
      });
    },
    editIncomeLiability (param) {
      param.liability.index = param.subindex;
      this.$bus.$emit('inquiry-liability-popup-show', param.liability, this.allBanks, this.allLiabilities, this.allCurrencies, this.allIncomeTypes, this.businessActivityCurrentIncomes, param.index);
    },
    removeIncomeLiability (param) {
      if (param.income) {
        Vue.swal({
          title: this.$t('inquiryFinancialInfo.areYouSureYouWantToRemoveCommitment'),
          text: '',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: this.$t('ui.yesRemove'),
          cancelButtonText: this.$t('ui.cancel')
        }).then((result) => {
          if (result.value) {
            param.income.liabilities.splice(param.liabilityIndex, 1);
            Vue.swal({
              title: this.$t('inquiryFinancialInfo.commitmentHasBeenRemove'),
              html: this.$t('inquiryFinancialInfo.offerMustBeRecorded'),
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: true
            })
          }
        });
      }
    },
    saveIncomeLiability (liabilityData, originalLiability, isLiabilityEdit, incomeIndex, originalIncomeIndex) {
      console.log(incomeIndex, originalIncomeIndex);
      let incomeWithLiability = this.data.incomes[incomeIndex];
      if (!isLiabilityEdit) {
        if (!incomeWithLiability.liabilities) {
          incomeWithLiability.liabilities = [];
        }

        incomeWithLiability.liabilities.push(liabilityData);
        return;
      }

      if (incomeIndex === originalIncomeIndex) {
        Object.assign(originalLiability, liabilityData);
        return;
      }

      this.data.incomes[originalIncomeIndex].liabilities.splice(originalLiability.index, 1);
      incomeWithLiability.liabilities.push(liabilityData);
    },
    getData () {
      let incomesOutput = [];
      for (let i = 0; i < this.data.incomes.length; i++) {
        incomesOutput.push(JSON.parse(JSON.stringify(this.data.incomes[i])));
      }

      return {
        incomes: incomesOutput
      };
    },
    loadAdditionalData() {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/industries/items',
        method: 'get',
        outputKey: 'allIndustries',
        loadedKey: 'industriesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/incomes-types/items',
        method: 'get',
        outputKey: 'allIncomeTypes',
        loadedKey: 'incomeTypesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/contracts-types/items',
        method: 'get',
        outputKey: 'allContractTypes',
        loadedKey: 'contractTypesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/system/settlements-types/items',
        method: 'get',
        outputKey: 'allSettlementTypes',
        loadedKey: 'settlementTypesLoaded',
        errorKey: 'loadError',
        params: {
          pagination: 0
        }
      });
    }
  },
  beforeDestroy() {
    this.$bus.$off('inquiry-income-popup-submit', this.saveIncome);
    this.$bus.$off('inquiry-income-edit', this.editIncome);
    this.$bus.$off('inquiry-income-remove', this.removeIncome);
    this.$bus.$off('inquiry-income-liability-edit', this.editIncomeLiability);
    this.$bus.$off('inquiry-income-liability-remove', this.removeIncomeLiability);
    this.$bus.$off('inquiry-income-liability-popup-submit', this.saveIncomeLiability);
  }
}
</script>

<style scoped lang="scss">
.f-input-wrapper.is-with-dropdown {
  .v-select {
    width: 140px;
  }
}
</style>
